import { useParams } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { type ComponentType, type ReactNode } from 'react';

import { YStack } from '@/components/ui/layout/layout';
import { SidebarRow } from '@/design-system-v2/sidebar-row';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';

import { getSettingsMenuContext } from './settings-menu-context';
import {
  SETTINGS_MENU_GROUPS,
  settingsMenuConfig,
} from './settings-menu.config';
import { openSettings } from './settings-navigation';

export const SettingsMenu = observer(function SettingsMenu() {
  const { section: currentSection = 'General' } = useParams({ strict: false });

  const handleMenuClick = (section: string) => {
    if (section === 'Subscription') {
      trackEvent('UPGRADE_PLAN_MENU_CLICKED');
    }
    void openSettings(section);
  };

  const menuContext = getSettingsMenuContext();
  const enabledMenuItems = settingsMenuConfig.filter(
    (item) => !item.enabled || item.enabled(menuContext)
  );

  return (
    <nav aria-label="Settings">
      {SETTINGS_MENU_GROUPS.map((group) => {
        const groupItems = enabledMenuItems.filter(
          (item) => item.group === group.id
        );
        if (groupItems.length === 0) return null;

        const headingId = `settings-menu-group-${group.id}`;

        return (
          <YStack
            key={group.id}
            role="group"
            aria-labelledby={headingId}
            className="w-full pb-4 last:pb-0"
          >
            <h2
              id={headingId}
              className="flex min-h-7 items-center px-[var(--row-pad)] text-sm text-foreground-muted"
            >
              {group.title}
            </h2>
            {groupItems.map((item) => (
              <SettingsMenuItem
                key={item.title}
                title={item.title}
                active={currentSection === item.hash.replace('settings/', '')}
                current={currentSection === item.hash.replace('settings/', '')}
                onClick={() =>
                  handleMenuClick(item.hash.replace('settings/', ''))
                }
                icon={item.icon}
              />
            ))}
          </YStack>
        );
      })}
    </nav>
  );
});

export type SettingsMenuIcon = ComponentType<{
  size?: number;
  className?: string;
}>;

type ISettingsMenuItem = {
  title: string;
  active?: boolean;
  current?: boolean;
  onClick?: () => void;
  icon: SettingsMenuIcon;
  trailing?: ReactNode;
};

export const SettingsMenuItem = ({
  title,
  active = false,
  current = false,
  onClick,
  icon: Icon,
  trailing,
}: ISettingsMenuItem) => (
  <SidebarRow.Button
    data-title={title}
    aria-current={current ? 'page' : undefined}
    selected={active}
    leading={
      <SidebarRow.Icon className={cn(active && 'text-foreground')}>
        <Icon />
      </SidebarRow.Icon>
    }
    trailing={trailing}
    onClick={onClick}
  >
    {title}
  </SidebarRow.Button>
);
